import { FeeConfigurationViewModel, FeeHudLineViewModel, FeeRulesVersionViewModel } from '../models/fees';

/**
 * Formats the version display for a fee configuration
 * @param feeConfiguration - The fee configuration to format
 * @param versions - Optional array of versions to look up the version number
 * @returns Version number as string or "Draft"
 */
export const formatFeeVersionDisplay = (
  feeConfiguration: FeeConfigurationViewModel,
  versions?: FeeRulesVersionViewModel[]
): string => {
  if (!feeConfiguration.feeRulesVersionId) {
    return 'Draft';
  }

  // If versions array is provided, look up the version number
  if (versions) {
    const version = versions.find((v) => v.feeRulesVersionId === feeConfiguration.feeRulesVersionId);
    if (version) {
      return `Version ${version.versionNumber.toString()}`;
    }
  }

  // Fallback to version ID if version not found
  return feeConfiguration.feeRulesVersionId.toString();
};

/**
 * Formats the HUD line label for display in dropdowns
 * @param hudLine - The HUD line to format
 * @returns Formatted label like "801 - Originating Fee (ID: 587)"
 */
export const formatHudLineLabel = (hudLine: FeeHudLineViewModel): string => {
  let label = '';
  if (hudLine.hudLine) {
    label = hudLine.hudLine;
    if (hudLine.description) {
      label += ` - ${hudLine.description}`;
    }
  } else {
    label = hudLine.description || `HUD Line ${hudLine.feeHudLineId}`;
  }
  label += ` (ID: ${hudLine.feeHudLineId})`;
  return label;
};

/**
 * Turns an existing fee configuration into seed data for a new draft copy: clears its identity
 * (id/guid/version), gives every rule a fresh temp id (matching the convention used when a user
 * copies a single rule in-place, `temp-${Date.now()}`), and points at the given draft HUD line —
 * a draft config must not reference a published (non-draft) HUD line row, which is what a
 * published source's own feeHudLineId points at.
 */
export const buildFeeConfigurationCopyDraft = (
  source: FeeConfigurationViewModel,
  draftHudLineId: number | null
): FeeConfigurationViewModel => {
  const cloned: FeeConfigurationViewModel = JSON.parse(JSON.stringify(source));
  cloned.feeConfigurationId = 0;
  cloned.feeConfigurationGuid = '';
  cloned.feeRulesVersionId = null;
  cloned.feeHudLineId = draftHudLineId;
  // A copied config starts ungrouped: the source's group id belongs to its own (possibly
  // published) version and would not resolve against the draft group tree.
  cloned.feeConfigGroupId = null;
  cloned.description = cloned.description ? `${cloned.description} (Copy)` : '(Copy)';
  cloned.rules = (cloned.rules ?? []).map((rule, index) => ({
    ...rule,
    ruleId: `temp-${Date.now()}-${index}`,
  }));
  return cloned;
};

